/**
 * Ventanas de **placas, tipos de placa y descuentos** del cliente.
 *
 * Portan las pantallas del panel anterior:
 * - `menu/cliente/placa.php` + `Cliente::placas` → **placas del cliente**.
 * - `menu/cliente/asignar_descuento.php` + `Cliente::crear_descuento_cliente` →
 *   **descuentos por producto** de una placa.
 * - los modales de tipos de placa y de «actualizar precio de descuento» del
 *   `menu/cliente/index.php` (`registrar_cliente_tipo_placa`,
 *   `actualizar_cliente_tipo_placa`,
 *   `actualizar_precio_descuento_placas_by_cliente_tipo_placa_id` y
 *   `actualizar_precio_descuento_all_placas`).
 *
 * Reglas conservadas:
 * - Las placas se dan de baja de forma **lógica** (`estado_placa = 2`); al darlas
 *   de baja se **desactivan** sus descuentos (el original los dejaba activos).
 * - El descuento de una placa es **por producto** y sólo cuenta si está activo:
 *   es lo que la venta resta por galón.
 * - El descuento en bloque **sólo actualiza** los descuentos que la placa ya
 *   tiene, no crea nuevos, como el original.
 */

import { useState } from 'react';
import { Plus, Tags, Trash2 } from 'lucide-react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { ApiError } from '@/lib/http/api-error';

import { monto } from '../gestion-formato';
import {
  useActualizarPlaca,
  useActualizarTipoPlaca,
  useAplicarDescuentoPorTipo,
  useAplicarDescuentosPorTipos,
  useCrearPlaca,
  useCrearTipoPlaca,
  useDescuentosDePlaca,
  useEliminarPlaca,
  useGuardarDescuentosPlaca,
  usePlacasDeCliente,
  useTiposPlaca,
} from '../clientes.api';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

interface AvisoProps {
  onAviso: (mensaje: string) => void;
  onClose: () => void;
}

/* ------------------------------------------------------------------ *
 * Placas del cliente
 * ------------------------------------------------------------------ */

export function ClientePlacasDialog({
  cliente,
  onAviso,
  onClose,
}: AvisoProps & { cliente: { id: number; nombre: string | null; numero_doc: string | null } | null }) {
  const consulta = usePlacasDeCliente(cliente?.id ?? null);
  const crear = useCrearPlaca(cliente?.id ?? null);
  const editar = useActualizarPlaca();
  const eliminar = useEliminarPlaca();

  const [formulario, setFormulario] = useState({
    id: undefined as number | undefined,
    numero_placa: '',
    descripcion_placa: '',
    cliente_tipo_placa_id: 0,
  });
  const [descuentos, setDescuentos] = useState<{ id: number; numero_placa: string } | null>(null);
  const [tipos, setTipos] = useState(false);
  const [porEliminar, setPorEliminar] = useState<{ id: number; numero_placa: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const placas = consulta.data?.placas ?? [];
  const tiposPlaca = consulta.data?.tipos_placa ?? [];

  const enviar = () => {
    setError(null);
    const manejar = {
      onSuccess: (respuesta: { mensaje: string }) => {
        onAviso(respuesta.mensaje);
        setFormulario({ id: undefined, numero_placa: '', descripcion_placa: '', cliente_tipo_placa_id: 0 });
      },
      onError: (fallo: unknown) =>
        setError(fallo instanceof ApiError ? fallo.message : 'No se pudo guardar la placa.'),
    };

    if (formulario.id === undefined) {
      crear.mutate(
        {
          numero_placa: formulario.numero_placa,
          descripcion_placa: formulario.descripcion_placa,
          cliente_tipo_placa_id: formulario.cliente_tipo_placa_id || null,
        },
        manejar,
      );
    } else {
      editar.mutate(
        {
          id: formulario.id,
          numero_placa: formulario.numero_placa,
          descripcion_placa: formulario.descripcion_placa,
          cliente_tipo_placa_id: formulario.cliente_tipo_placa_id || null,
        },
        manejar,
      );
    }
  };

  return (
    <Dialog
      open={cliente !== null}
      onClose={onClose}
      size="xl"
      title={`Placas de ${cliente?.nombre ?? 'el cliente'}`}
      description="Cada placa lleva su descuento por producto, que es lo que la venta resta por galón."
      footer={
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cerrar
        </Button>
      }
    >
      {error ? (
        <Alert variant="error" title="No se pudo guardar">
          {error}
        </Alert>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Input
          label="Número de placa"
          value={formulario.numero_placa}
          onChange={(evento) =>
            setFormulario((previo) => ({ ...previo, numero_placa: evento.target.value.toUpperCase() }))
          }
        />
        <Input
          label="Descripción"
          value={formulario.descripcion_placa}
          onChange={(evento) => setFormulario((previo) => ({ ...previo, descripcion_placa: evento.target.value }))}
        />
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-ink-700">Tipo de placa</span>
          <Select
            className={CONTROL}
            value={formulario.cliente_tipo_placa_id}
            onChange={(evento) =>
              setFormulario((previo) => ({ ...previo, cliente_tipo_placa_id: Number(evento.target.value) }))
            }
          >
            <option value={0}>Sin tipo</option>
            {tiposPlaca.map((tipo) => (
              <option key={tipo.id} value={tipo.id}>
                {tipo.descripcion}
              </option>
            ))}
          </Select>
        </label>

        <div className="flex flex-wrap items-end gap-2">
          <Button
            size="sm"
            loading={crear.isPending || editar.isPending}
            leftIcon={<Plus className="size-4" />}
            onClick={enviar}
          >
            {formulario.id === undefined ? 'Añadir placa' : 'Guardar placa'}
          </Button>
          {formulario.id !== undefined ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setFormulario({ id: undefined, numero_placa: '', descripcion_placa: '', cliente_tipo_placa_id: 0 })
              }
            >
              Nueva
            </Button>
          ) : null}
          <Button variant="outline" size="sm" leftIcon={<Tags className="size-4" />} onClick={() => setTipos(true)}>
            Tipos de placa
          </Button>
        </div>
      </div>

      {consulta.isLoading ? <Skeleton className="h-40 w-full" /> : null}

      <ul className="max-h-[45vh] space-y-2 overflow-auto">
        {placas.map((placa) => (
          <li key={placa.id} className="rounded-card border border-ink-100 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-semibold text-ink-900">
                  {placa.numero_placa}
                  {placa.cliente_tipo_placa ? (
                    <span className="ml-2 text-xs font-normal text-ink-500">{placa.cliente_tipo_placa}</span>
                  ) : null}
                </p>
                <p className="text-xs text-ink-500">
                  {placa.descripcion_placa || 'Sin descripción'} · {placa.descuentos.length} descuento
                  {placa.descuentos.length === 1 ? '' : 's'} activo
                  {placa.descuentos.length === 1 ? '' : 's'}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDescuentos({ id: placa.id, numero_placa: placa.numero_placa })}
                >
                  Descuentos
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setFormulario({
                      id: placa.id,
                      numero_placa: placa.numero_placa,
                      descripcion_placa: placa.descripcion_placa ?? '',
                      cliente_tipo_placa_id: placa.cliente_tipo_placa_id ?? 0,
                    })
                  }
                >
                  Editar
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Trash2 className="size-4" />}
                  onClick={() => setPorEliminar({ id: placa.id, numero_placa: placa.numero_placa })}
                >
                  Eliminar
                </Button>
              </div>
            </div>

            {placa.descuentos.length ? (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {placa.descuentos.map((descuento) => (
                  <Badge key={descuento.id} variant="brand">
                    {descuento.producto_nombre}: {monto(Number(descuento.descuento))}
                  </Badge>
                ))}
              </div>
            ) : null}
          </li>
        ))}
      </ul>

      {!consulta.isLoading && placas.length === 0 ? (
        <p className="rounded-card border border-dashed border-ink-200 p-6 text-center text-sm text-ink-500">
          Este cliente no tiene placas registradas.
        </p>
      ) : null}

      <ClienteDescuentosDialog
        placa={descuentos}
        onClose={() => setDescuentos(null)}
        onAviso={onAviso}
      />

      <TiposPlacaDialog abierto={tipos} onClose={() => setTipos(false)} onAviso={onAviso} />

      <ConfirmDialog
        open={porEliminar !== null}
        title={`¿Eliminar la placa ${porEliminar?.numero_placa ?? ''}?`}
        description="Se da de baja la placa y se desactivan sus descuentos; el historial de ventas no se toca."
        confirmLabel="Eliminar placa"
        tone="danger"
        loading={eliminar.isPending}
        onClose={() => setPorEliminar(null)}
        onConfirm={() => {
          const placa = porEliminar;

          setPorEliminar(null);

          if (!placa) return;

          eliminar.mutate(placa.id, {
            onSuccess: (respuesta) => onAviso(respuesta.mensaje),
            onError: (fallo) =>
              setError(fallo instanceof ApiError ? fallo.message : 'No se pudo eliminar la placa.'),
          });
        }}
      />
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Descuentos de una placa
 * ------------------------------------------------------------------ */

export function ClienteDescuentosDialog({
  placa,
  onAviso,
  onClose,
}: AvisoProps & { placa: { id: number; numero_placa: string } | null }) {
  const consulta = useDescuentosDePlaca(placa?.id ?? null);
  const guardar = useGuardarDescuentosPlaca(placa?.id ?? null);

  const [borrador, setBorrador] = useState<Record<number, { descuento: number; activo: boolean }>>({});
  const [firma, setFirma] = useState('');
  const [error, setError] = useState<string | null>(null);

  const productos = consulta.data?.productos ?? [];

  // El listado rellena el borrador en el render (guarda `firma`), sin efectos.
  if (consulta.data && firma !== String(placa?.id)) {
    setFirma(String(placa?.id));
    setBorrador(
      Object.fromEntries(
        consulta.data.productos.map((producto) => [
          producto.id,
          { descuento: Number(producto.descuento ?? 0), activo: producto.activo },
        ]),
      ),
    );
    setError(null);
  }

  const enviar = () => {
    setError(null);
    guardar.mutate(
      {
        descuentos: Object.entries(borrador).map(([productoId, valor]) => ({
          producto_id: Number(productoId),
          descuento: valor.descuento,
          activo: valor.activo,
        })),
      },
      {
        onSuccess: (respuesta) => onAviso(respuesta.mensaje),
        onError: (fallo) =>
          setError(fallo instanceof ApiError ? fallo.message : 'No se pudieron guardar los descuentos.'),
      },
    );
  };

  return (
    <Dialog
      open={placa !== null}
      onClose={onClose}
      size="xl"
      title={`Descuentos de la placa ${placa?.numero_placa ?? ''}`}
      description="Marca los productos con descuento y pon cuánto se rebaja por galón. Los que queden sin marcar se desactivan sin borrarse."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cerrar
          </Button>
          <Button size="sm" loading={guardar.isPending} onClick={enviar}>
            Guardar descuentos
          </Button>
        </>
      }
    >
      {error ? (
        <Alert variant="error" title="No se pudieron guardar">
          {error}
        </Alert>
      ) : null}

      <Alert variant="info" title="Cómo se aplica">
        <span className={ICONO_EN_ALERTA}>
          El descuento se resta al <strong>precio por galón</strong> del producto en las ventas que
          lleven esta placa. Los descuentos de un tipo de placa se pueden repartir en bloque desde
          «Tipos de placa».
        </span>
      </Alert>

      {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

      {productos.length ? (
        <div className="max-h-[50vh] overflow-auto rounded-card border border-ink-100">
          <table className="w-full border-collapse text-[13px]">
            <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
              <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                <th className="px-2 py-2">Aplica</th>
                <th className="px-2 py-2">Producto</th>
                <th className="px-2 py-2">Tipo</th>
                <th className="px-2 py-2 text-right">Precio de venta</th>
                <th className="px-2 py-2 text-right">Descuento por galón</th>
              </tr>
            </thead>
            <tbody>
              {productos.map((producto) => {
                const valor = borrador[producto.id] ?? { descuento: 0, activo: false };

                return (
                  <tr key={producto.id} className="border-t border-ink-100 hover:bg-brand-50/30">
                    <td className="px-2 py-2">
                      <input
                        type="checkbox"
                        className="size-4 accent-brand-500"
                        checked={valor.activo}
                        aria-label={`Aplicar descuento a ${producto.nombre}`}
                        onChange={(evento) =>
                          setBorrador((previo) => ({
                            ...previo,
                            [producto.id]: { ...valor, activo: evento.target.checked },
                          }))
                        }
                      />
                    </td>
                    <td className="px-2 py-2 font-semibold text-ink-900">{producto.nombre}</td>
                    <td className="px-2 py-2 text-xs text-ink-500">{producto.tipo ?? '—'}</td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      {monto(Number(producto.precio_venta))}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <input
                        className="h-9 w-28 rounded-control border border-ink-200 px-2 text-right text-sm"
                        type="number"
                        step="0.01"
                        disabled={!valor.activo}
                        value={valor.descuento}
                        aria-label={`Descuento de ${producto.nombre}`}
                        onChange={(evento) =>
                          setBorrador((previo) => ({
                            ...previo,
                            [producto.id]: { ...valor, descuento: Number(evento.target.value) },
                          }))
                        }
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Tipos de placa
 * ------------------------------------------------------------------ */

export function TiposPlacaDialog({ abierto, onAviso, onClose }: AvisoProps & { abierto: boolean }) {
  const consulta = useTiposPlaca(abierto);
  const crear = useCrearTipoPlaca();
  const editar = useActualizarTipoPlaca();
  const porTipo = useAplicarDescuentoPorTipo();
  const porMapa = useAplicarDescuentosPorTipos();

  const [formulario, setFormulario] = useState({ id: undefined as number | undefined, descripcion: '' });
  const [descuentos, setDescuentos] = useState<Record<number, number>>({});
  const [error, setError] = useState<string | null>(null);
  const [resultado, setResultado] = useState<string | null>(null);

  const tipos = consulta.data?.tipos ?? [];

  const guardar = () => {
    setError(null);
    const manejar = {
      onSuccess: (respuesta: { mensaje: string }) => {
        onAviso(respuesta.mensaje);
        setFormulario({ id: undefined, descripcion: '' });
      },
      onError: (fallo: unknown) =>
        setError(fallo instanceof ApiError ? fallo.message : 'No se pudo guardar el tipo de placa.'),
    };

    if (formulario.id === undefined) crear.mutate({ descripcion: formulario.descripcion }, manejar);
    else editar.mutate({ id: formulario.id, descripcion: formulario.descripcion }, manejar);
  };

  return (
    <Dialog
      open={abierto}
      onClose={onClose}
      size="lg"
      title="Tipos de placa"
      description="El descuento por tipo se reparte a todas las placas de ese tipo, o con el mapa a todos los tipos a la vez."
      footer={
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cerrar
        </Button>
      }
    >
      {error ? (
        <Alert variant="error" title="No se pudo completar">
          {error}
        </Alert>
      ) : null}

      {resultado ? (
        <Alert variant="success" title="Descuentos actualizados">
          {resultado}
        </Alert>
      ) : null}

      <div className="flex flex-wrap items-end gap-2">
        <Input
          label="Descripción"
          containerClassName="min-w-[220px] flex-1"
          value={formulario.descripcion}
          onChange={(evento) => setFormulario((previo) => ({ ...previo, descripcion: evento.target.value }))}
        />
        <Button size="sm" loading={crear.isPending || editar.isPending} onClick={guardar}>
          {formulario.id === undefined ? 'Añadir tipo' : 'Guardar tipo'}
        </Button>
        {formulario.id !== undefined ? (
          <Button variant="ghost" size="sm" onClick={() => setFormulario({ id: undefined, descripcion: '' })}>
            Nuevo
          </Button>
        ) : null}
      </div>

      {consulta.isLoading ? <Skeleton className="h-40 w-full" /> : null}

      <ul className="space-y-2">
        {tipos.map((tipo) => (
          <li key={tipo.id} className="rounded-card border border-ink-100 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-semibold text-ink-900">{tipo.descripcion}</p>
                <p className="text-xs text-ink-500">
                  {tipo.placas} placa{tipo.placas === 1 ? '' : 's'} de este tipo
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={tipo.estado ? 'success' : 'neutral'}>
                  {tipo.estado ? 'ACTIVO' : 'INACTIVO'}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFormulario({ id: tipo.id, descripcion: tipo.descripcion })}
                >
                  Editar
                </Button>
              </div>
            </div>

            <div className="mt-2 flex flex-wrap items-end gap-2">
              <Input
                label="Descuento por galón para este tipo"
                type="number"
                step="0.01"
                containerClassName="w-40"
                value={descuentos[tipo.id] ?? ''}
                onChange={(evento) =>
                  setDescuentos((previo) => ({ ...previo, [tipo.id]: Number(evento.target.value) }))
                }
              />
              <Button
                variant="outline"
                size="sm"
                loading={porTipo.isPending}
                onClick={() =>
                  porTipo.mutate(
                    { tipoId: tipo.id, descuento: descuentos[tipo.id] ?? 0 },
                    {
                      onSuccess: (respuesta) => {
                        setResultado(respuesta.mensaje);
                        onAviso(respuesta.mensaje);
                      },
                      onError: (fallo) =>
                        setError(fallo instanceof ApiError ? fallo.message : 'No se pudo aplicar.'),
                    },
                  )
                }
              >
                Aplicar a sus placas
              </Button>
            </div>
          </li>
        ))}
      </ul>

      {tipos.length > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-ink-100 bg-ink-50/40 p-3">
          <p className="text-sm text-ink-600">
            Aplica a la vez el descuento puesto en cada tipo, a <strong>todas</strong> las placas.
          </p>
          <Button
            size="sm"
            loading={porMapa.isPending}
            onClick={() =>
              porMapa.mutate(
                {
                  descuentos: tipos
                    .filter((tipo) => descuentos[tipo.id] !== undefined)
                    .map((tipo) => ({ tipo_placa_id: tipo.id, descuento: descuentos[tipo.id] ?? 0 })),
                },
                {
                  onSuccess: (respuesta) => {
                    setResultado(respuesta.mensaje);
                    onAviso(respuesta.mensaje);
                  },
                  onError: (fallo) =>
                    setError(fallo instanceof ApiError ? fallo.message : 'No se pudieron aplicar.'),
                },
              )
            }
          >
            Aplicar a todos los tipos
          </Button>
        </div>
      ) : null}
    </Dialog>
  );
}
